import axios from 'axios';

const SERVER_URL = process.env.REACT_APP_API_URL || 'http://localhost:1337';

// Set up axios response interceptor to handle 401 errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear authentication state and redirect to login
      // Note: We don't import useAuthStore here to avoid circular dependencies
      // The interceptor will be handled by the main axios instance
    }
    return Promise.reject(error);
  }
);

export interface Certificate {
  id: number;
  name: string;
  issueDate: string;
  courseId: string;
  courseName: string;
  certificateUrl?: string;
  course?: any;
  certificateNumber?: string;
  // Additional fields that might come from the API
  description?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CertificateListResponse {
  data: Certificate[];
  meta?: {
    pagination?: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

export interface SingleCertificateResponse {
  data: Certificate;
  meta?: any;
}

/**
 * Get all certificates with optional pagination and filters
 * GET /api/certificates
 */
export const getCertificates = async (params?: {
  page?: number;
  pageSize?: number;
  sort?: string;
  filters?: Record<string, any>;
}): Promise<CertificateListResponse> => {
  try {
    const token = localStorage.getItem('jwt_token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('pagination[page]', params.page.toString());
    if (params?.pageSize) queryParams.append('pagination[pageSize]', params.pageSize.toString());
    if (params?.sort) queryParams.append('sort', params.sort);

    // Add filters if provided
    if (params?.filters) {
      Object.entries(params.filters).forEach(([key, value]) => {
        queryParams.append(`filters[${key}]`, value.toString());
      });
    }

    const url = `${SERVER_URL}/api/certificates${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching certificates:', error);
    throw error;
  }
};

/**
 * Get a specific certificate by ID
 * GET /api/certificates/:id
 */
export const getCertificate = async (id: number): Promise<SingleCertificateResponse> => {
  try {
    const token = localStorage.getItem('jwt_token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get(`${SERVER_URL}/api/certificates/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error) {
    console.error(`Error fetching certificate ${id}:`, error);
    throw error;
  }
};

/**
 * Get current user's certificates
 * GET /api/certificates/my
 */
export const getMyCertificates = async (): Promise<CertificateListResponse> => {
  try {
    const token = localStorage.getItem('jwt_token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get(`${SERVER_URL}/api/certificates/my`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching user certificates:', error);
    throw error;
  }
};

/**
 * Helper function to analyze server response and convert to proper format
 */
const analyzeAndConvertResponse = (data: any): Blob => {
  if (data instanceof Blob) {
    return data;
  }

  if (data && typeof data === 'object') {
    // Check if it's a byte array object
    const keys = Object.keys(data);
    const values = Object.values(data);

    // Validate that all values are numbers
    const allNumbers = values.every(val => typeof val === 'number' && val >= 0 && val <= 255);

    if (allNumbers && keys.length > 0) {
      // Convert to Uint8Array
      const byteArray = values as number[];
      const uint8Array = new Uint8Array(byteArray);

      // Validate PDF header
      const firstBytes = byteArray.slice(0, 4);
      const pdfHeader = [37, 80, 68, 70]; // %PDF in ASCII
      const isPdf = firstBytes.every((byte, index) => byte === pdfHeader[index]);

      if (isPdf) {
        return new Blob([uint8Array], { type: 'application/pdf' });
      } else {
        // Still try to create blob, but warn user
        return new Blob([uint8Array], { type: 'application/octet-stream' });
      }
    }
  }

  throw new Error('Unable to convert server response to downloadable format');
};

/**
 * Download a certificate by ID
 * GET /api/certificates/:id/download
 */
export const downloadCertificate = async (id: number): Promise<{ blob: Blob; filename: string }> => {
  try {
    const token = localStorage.getItem('jwt_token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get(`${SERVER_URL}/api/certificates/${id}/download`, {
      headers: {
        Authorization: `Bearer ${token}`
      },
      responseType: 'json' // Changed from 'blob' to handle JSON response
    });

    // Extract filename from Content-Disposition header if available
    const contentDisposition = response.headers['content-disposition'];
    let filename = `certificate-${id}.pdf`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    // Convert response to blob
    const blob = analyzeAndConvertResponse(response.data);

    return { blob, filename };
  } catch (error) {
    console.error(`Error downloading certificate ${id}:`, error);
    throw error;
  }
};

/**
 * Helper function to trigger certificate download in browser
 */
export const downloadCertificateFile = async (id: number, filename?: string): Promise<void> => {
  try {
    const { blob, filename: downloadedFilename } = await downloadCertificate(id);

    // Validate blob before download
    if (!blob || blob.size === 0) {
      throw new Error('Invalid certificate file received');
    }

    // Create a download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || downloadedFilename;

    // Add event listener to detect if download actually started
    let downloadStarted = false;
    link.addEventListener('click', () => {
      downloadStarted = true;
    });

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    // Small delay to ensure download has time to start
    await new Promise(resolve => setTimeout(resolve, 100));

    if (!downloadStarted) {
      throw new Error('Download failed to start. Please check your browser settings.');
    }
  } catch (error) {
    console.error('Error downloading certificate file:', error);
    throw error;
  }
};

/**
 * Check if the browser supports file downloads
 */
export const isDownloadSupported = (): boolean => {
  return 'download' in document.createElement('a');
};

/**
 * Alternative download method for browsers that don't support the download attribute
 */
export const downloadCertificateAlternative = async (id: number, filename?: string): Promise<void> => {
  try {
    const { blob } = await downloadCertificate(id);

    // Open in new tab for browsers that don't support download attribute
    const url = window.URL.createObjectURL(blob);
    const newWindow = window.open(url);

    if (!newWindow) {
      // If popup blocked, fall back to download method
      await downloadCertificateFile(id, filename);
    } else {
      // Clean up URL after a delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    }
  } catch (error) {
    console.error('Error with alternative download method:', error);
    throw error;
  }
};
